import React from "react";

import AfriMedLandingShell from "@/components/layout/AfriMedLandingShell.jsx";
import Radio from "@/components/ui/Radio.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  normalizeStagingEmail,
  normalizeStagingJourney,
  requestStagingAccess,
} from "@/services/stagingAccessService.js";

function buildRequestContext() {
  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search || "")
      : new URLSearchParams();
  const referrer =
    typeof document !== "undefined" ? String(document.referrer || "").trim() : "";
  const userAgent =
    typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "";
  const width = typeof window !== "undefined" ? Number(window.innerWidth || 0) : 0;

  return {
    source: String(params.get("source") || params.get("utm_source") || "").trim(),
    referrer,
    device: {
      kind: width >= 1024 ? "desktop" : width >= 640 ? "tablet" : "mobile",
      platform: typeof navigator !== "undefined" ? String(navigator.platform || "") : "",
      userAgent,
      language: typeof navigator !== "undefined" ? String(navigator.language || "") : "",
      viewportWidth: width,
      viewportHeight: typeof window !== "undefined" ? Number(window.innerHeight || 0) : 0,
      touchPoints:
        typeof navigator !== "undefined" ? Number(navigator.maxTouchPoints || 0) : 0,
    },
  };
}

export default function StagingSignupPage() {
  const { t } = useLanguage();
  const [email, setEmail] = React.useState("");
  const [journey, setJourney] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState("");
  const [error, setError] = React.useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting || submitted) return;

    const nextEmail = normalizeStagingEmail(email);
    if (!nextEmail) {
      setError("Enter your email before continuing.");
      return;
    }
    const nextJourney = normalizeStagingJourney(journey);
    if (!nextJourney) {
      setError(
        t(
          "stagingAccess.errors.journeyRequired",
          "Choose the Patient, Clinician, or Research • Public health journey.",
        ),
      );
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const result = await requestStagingAccess({
        email: nextEmail,
        journey: nextJourney,
        path: "/signup",
        context: buildRequestContext(),
      });
      if (result?.ok !== true) {
        throw new Error(
          result?.message ||
            "AfriMed could not send a secure access email right now. Please try again later.",
        );
      }
      setConfirmation(
        result?.message ||
          "If this email is authorised for staging, AfriMed will send a secure access link.",
      );
      setSubmitted(true);
    } catch (nextError) {
      setError(
        nextError?.message ||
          "AfriMed could not save your staging request right now.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AfriMedLandingShell
      title={t("stagingAccess.title", "Private staging access")}
      subtitleClassName="max-w-[390px]"
      subtitle={(
        <>
          <span className="block">
            Enter your email and choose the AfriMed™
          </span>
          <span className="block">journey you would like to explore.</span>
        </>
      )}
    >
      <form className="space-y-4 text-center" onSubmit={handleSubmit}>
        <TextField56
          label={t("stagingAccess.email", "Email")}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          placeholder="name@example.com"
          tone="surface"
          disabled={submitted}
        />

        <fieldset className="space-y-2 text-center" disabled={submitted}>
          <legend className="w-full text-label-large text-on-surface">
            {t("stagingAccess.journeyLegend", "I’m exploring AfriMed™ as")}
          </legend>
          <div
            role="radiogroup"
            aria-label={t("stagingAccess.journeyLegend", "I’m exploring AfriMed™ as")}
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
          >
            <div
              className="flex shrink-0 cursor-pointer items-center gap-2 py-1"
              onClick={() => setJourney("patient")}
            >
              <Radio
                variant="afrimed"
                name={t("stagingAccess.patient", "Patient")}
                checked={journey === "patient"}
                onChange={() => setJourney("patient")}
                disabled={submitted}
              />
              <span className="text-body-large text-on-surface">
                {t("stagingAccess.patient", "Patient")}
              </span>
            </div>
            <div
              className="flex shrink-0 cursor-pointer items-center gap-2 py-1"
              onClick={() => setJourney("clinician")}
            >
              <Radio
                variant="afrimed"
                name={t("stagingAccess.clinician", "Clinician")}
                checked={journey === "clinician"}
                onChange={() => setJourney("clinician")}
                disabled={submitted}
              />
              <span className="text-body-large text-on-surface">
                {t("stagingAccess.clinician", "Clinician")}
              </span>
            </div>
            <div
              className="flex max-w-full cursor-pointer items-center gap-2 py-1"
              onClick={() => setJourney("research")}
            >
              <Radio
                variant="afrimed"
                name={t("stagingAccess.research", "Research • Public health")}
                checked={journey === "research"}
                onChange={() => setJourney("research")}
                disabled={submitted}
              />
              <span className="text-body-large text-on-surface">
                {t("stagingAccess.research", "Research • Public health")}
              </span>
            </div>
          </div>
        </fieldset>

        {error ? (
          <div className="rounded-[14px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
            {error}
          </div>
        ) : null}

        {submitted ? (
          <div className="whitespace-pre-line rounded-[14px] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3 text-body-medium text-[rgb(var(--afrimed-primary))]">
            {confirmation || t("stagingAccess.confirmation", "Check your email to continue.")}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={submitting || submitted}
          className="inline-flex h-12 w-full items-center justify-center rounded-[14px] bg-[rgb(var(--afrimed-primary))] px-5 text-title-medium text-[rgb(var(--afrimed-on-primary))] transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-55"
        >
          {submitting
            ? t("stagingAccess.requesting", "Requesting access...")
            : t("stagingAccess.request", "Sign me up")}
        </button>
      </form>
    </AfriMedLandingShell>
  );
}
